import type { AppSnapshot, Track, SortOrder } from "./types.js";
import type { icons } from "./icon-paths.js";
import React, { useLayoutEffect, useRef, useSyncExternalStore } from "react";
import { actions, canCloseDialog } from "./controller.js";
import { appStore, playbackStore, dialogStore, toastStore } from "./store.js";
import { Icon } from "./icons.js";
import { Guide } from "./Guide.js";
import { toggleTheme, useTheme } from "./theme.js";
import { coverURI } from "../../dist/creative-core.js";
import { AUDIO_ACCEPT, formatLabel } from "../../dist/format-core.js";

const colors = [
  "#ff0155",
  "#242424",
  "#9d9d9d",
  "#d67a98",
  "#656565",
  "#b9b9b9",
];
const duration = (seconds: number) =>
  !Number.isFinite(Number(seconds)) || seconds <= 0
    ? "—"
    : `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
const cx = (base: string, hidden: boolean) =>
  `${base}${hidden ? " hidden" : ""}`;
// Handle sync validation failures and async failures consistently at React event boundaries.
export function run<Args extends unknown[]>(
  action: (...args: Args) => unknown,
  ...args: Args
) {
  try {
    Promise.resolve(action(...args)).catch(actions.reportError);
  } catch (error) {
    actions.reportError(error);
  }
}
const on =
  <Args extends unknown[]>(action: (...args: Args) => unknown, ...args: Args) =>
  () =>
    run(action, ...args);
const pickFile = (id: string) => document.getElementById(id)?.click();

function Sidebar({ state }: { state: AppSnapshot }) {
  const items: Array<[string, keyof typeof icons, string, string | number]> = [
    ["home", "spark", "Home", ""],
    ["all", "music", "Library", state.tracks.length],
    [
      "favorites",
      "heart",
      "Favorites",
      state.tracks.filter((t) => t.favorite).length,
    ],
    ["recent", "clock", "Recently added", ""],
    ["how-it-works", "help", "How this works", ""],
  ];
  return (
    <>
      <aside
        className={`sidebar${state.sidebarOpen ? " open" : ""}`}
        id="sidebar"
      >
        <a
          className="brand"
          href="#"
          aria-label="Crate home"
          onClick={(e) => {
            e.preventDefault();
            actions.navigate("home");
          }}
        >
          <span className="brand-record" />
          crate<span className="brand-dot">.</span>
        </a>
        <p className="nav-label">LIBRARY</p>
        <nav id="library-nav">
          {items.map(([id, icon, label, count]) => (
            <button
              key={id}
              className={`nav-item ${state.view === id ? "active" : ""}`}
              data-view={id}
              onClick={on(actions.navigate, id)}
            >
              <Icon name={icon} />
              <span>{label}</span>
              <span className="nav-count">{count}</span>
            </button>
          ))}
        </nav>
        <div className="nav-label sets-label">
          SETS
          <button
            className="icon-button"
            id="sidebar-new"
            aria-label="Create a set"
            onClick={on(actions.createSet)}
          >
            +
          </button>
        </div>
        <nav id="sets-nav">
          {state.sets.map((set) => (
            <button
              key={set.id}
              className={`nav-item ${state.view === set.id ? "active" : ""}`}
              data-view={set.id}
              onClick={on(actions.navigate, set.id)}
            >
              <span
                className="set-dot"
                style={{ background: colors[set.color % 6] }}
              />
              <span>{set.name}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <button
            id="settings"
            className="text-button"
            aria-label="Library settings"
            onClick={on(actions.settings)}
          >
            <Icon name="settings" />
            Settings
          </button>
        </div>
      </aside>
      <button
        className="sidebar-backdrop"
        id="sidebar-backdrop"
        aria-label="Close navigation"
        tabIndex={-1}
        onClick={() => {
          actions.closeSidebar();
          document
            .getElementById("menu-button")
            ?.focus({ preventScroll: true });
        }}
      />
    </>
  );
}
function Header({ state }: { state: AppSnapshot }) {
  const theme = useTheme();
  const breadcrumb =
    state.set?.name ||
    {
      home: "Home",
      all: "Library",
      favorites: "Favorites",
      recent: "Recently added",
      sets: "Sets",
      "how-it-works": "How this works",
    }[state.view];
  return (
    <header className="topbar">
      <button
        className="icon-button mobile-menu"
        id="menu-button"
        aria-label="Toggle navigation"
        aria-expanded={state.sidebarOpen}
        aria-controls="sidebar"
        onClick={on(actions.toggleSidebar)}
      >
        <Icon name="menu" />
      </button>
      <a
        href="#"
        className="top-wordmark"
        aria-label="Crate home"
        onClick={(e) => {
          e.preventDefault();
          actions.navigate("home");
        }}
      >
        crate<span>®</span>
      </a>
      <div className="breadcrumb">
        <strong id="breadcrumb">{breadcrumb}</strong>
      </div>
      <div className="topbar-right">
        <button
          className="icon-button theme-toggle"
          id="theme-toggle"
          aria-label="Dark mode"
          aria-pressed={theme === "dark"}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          onClick={toggleTheme}
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} />
        </button>
        <button
          className="icon-button"
          id="help-button"
          aria-label="How Crate works"
          onClick={on(actions.about)}
        >
          <Icon name="help" />
        </button>
      </div>
    </header>
  );
}
function PageHeading({ state }: { state: AppSnapshot }) {
  const { view, set } = state,
    home = view === "home",
    setsView = view === "sets";
  return (
    <section
      className={cx(
        `page-heading${set ? " set-heading" : ""}`,
        view === "how-it-works",
      )}
    >
      <div>
        <h1 id="page-title">
          {set
            ? set.name
            : home
              ? "Home"
              : setsView
                ? "Sets"
                : view === "favorites"
                  ? "Favorites"
                  : view === "recent"
                    ? "Recently added"
                    : "Library"}
        </h1>
        {set?.description && <p id="page-description">{set.description}</p>}
      </div>
      <button
        className={cx("button primary", setsView)}
        id="upload-button"
        disabled={state.loading}
        onClick={on(actions.addSongs)}
      >
        <Icon name={set ? "list" : "upload"} />
        {set ? "Add songs" : "Add music"}
      </button>
    </section>
  );
}
function Home({ state }: { state: AppSnapshot }) {
  const featured =
    state.sets.find((s) => s.id === state.lastSetId) ||
    state.sets.find((s) => !s.id.startsWith("set-")) ||
    state.sets[0];
  return (
    <section
      id="creative-home"
      className={cx("creative-home", state.view !== "home")}
      aria-label="Home"
    >
      <div className="home-layout">
        <div className="home-intro">
          <h2>Your sets</h2>
          <p>
            {state.sets.length} {state.sets.length === 1 ? "set" : "sets"} ·{" "}
            {state.tracks.length} {state.tracks.length === 1 ? "song" : "songs"}
          </p>
          <button
            className="button primary"
            id="home-create"
            disabled={state.loading}
            onClick={on(actions.createSet)}
          >
            Create a set <Icon name="next" />
          </button>
          <button
            className="home-library-link text-button"
            data-view="all"
            onClick={on(actions.navigate, "all")}
          >
            Open library <span>↗</span>
          </button>
        </div>
        {featured ? (
          <button
            className="featured-set"
            data-view={featured.id}
            onClick={on(actions.navigate, featured.id)}
          >
            <img src={coverURI(featured)} alt={`Cover for ${featured.name}`} />
            <span className="featured-caption">
              <span>
                <small>
                  {featured.trackIds.some(
                    (id) => state.tracks.find((t) => t.id === id)?.demo,
                  )
                    ? "DEMO SET"
                    : "LAST OPENED"}
                </small>
                <strong>{featured.name}</strong>
              </span>
              <span>↗</span>
            </span>
          </button>
        ) : null}
      </div>
      <div className="home-utilities">
        <button
          className="home-guide-link"
          data-view="how-it-works"
          onClick={on(actions.navigate, "how-it-works")}
        >
          <Icon name="help" />
          <span>
            <strong>Help</strong>
          </span>
          <span>↗</span>
        </button>
        <button
          id="home-backup"
          disabled={state.loading}
          onClick={on(actions.backupDialog)}
        >
          <Icon name="drive" />
          <span>
            <strong>Back up library</strong>
          </span>
          <span>↗</span>
        </button>
      </div>
    </section>
  );
}
function SetStage({ state }: { state: AppSnapshot }) {
  const set = state.set,
    available = set ? state.scoped : [],
    seconds = available.reduce((s, t) => s + (Number(t.duration) || 0), 0);
  return (
    <section
      id="set-stage"
      className={cx("set-stage", !set)}
      aria-label="Set artwork and actions"
    >
      {set && (
        <>
          <img
            className="stage-art"
            src={coverURI(set)}
            alt={`Artwork for ${set.name}`}
          />
          <div className="stage-copy">
            {set.label && <span className="micro-label">{set.label}</span>}
            <p>
              {available.length} songs
              {seconds ? ` · ${Math.round(seconds / 60)} min` : ""}
            </p>
            <div className="stage-actions">
              <button
                className="button primary"
                id="stage-listen"
                onClick={on(actions.listenSet)}
              >
                <Icon name="play" /> Listen
              </button>
              <button
                className="button secondary"
                id="stage-arrange"
                onClick={on(actions.arrangeSet)}
              >
                <Icon name="list" /> Arrange
              </button>
            </div>
            <button
              className="text-button"
              id="stage-cover"
              onClick={on(actions.saveSetCover)}
            >
              Save cover image <span>↓</span>
            </button>
          </div>
        </>
      )}
    </section>
  );
}
function Sets({ state }: { state: AppSnapshot }) {
  return (
    <section
      className={cx("sets-section", state.view !== "sets")}
      id="sets-section"
    >
      <div className="section-heading">
        <h2>
          Sets <span id="set-count">{state.sets.length}</span>
        </h2>
        <button
          className="text-button"
          id="new-set"
          onClick={on(actions.createSet)}
        >
          New set <span>+</span>
        </button>
      </div>
      <div className="set-grid" id="set-grid">
        {state.sets.length ? (
          state.sets.map((set) => (
            <button
              key={set.id}
              className="set-card"
              data-view={set.id}
              onClick={on(actions.navigate, set.id)}
            >
              <div className="set-art">
                <img src={coverURI(set)} alt="" />
              </div>
              <div className="set-info">
                <strong>{set.name}</strong>
                <p>
                  {set.trackIds.length} songs
                  {set.label ? ` · ${set.label}` : ""}
                </p>
              </div>
              <span className="set-arrow">↗</span>
            </button>
          ))
        ) : (
          <button
            className="set-card new-set-card"
            id="first-set"
            onClick={on(actions.createSet)}
          >
            <span>+</span>
            <strong>Create a set</strong>
          </button>
        )}
      </div>
    </section>
  );
}
function Filters({ state }: { state: AppSnapshot }) {
  return (
    <div className="toolbar">
      <div className="search-wrap">
        <span>
          <Icon name="search" />
        </span>
        <input
          id="search"
          type="search"
          placeholder="Search tracks, artists, albums…"
          aria-label="Search tracks"
          value={state.filters.search}
          onChange={(e) => actions.setFilters({ search: e.target.value })}
        />
      </div>
      <button
        className="filter-toggle"
        id="filter-toggle"
        aria-label="Filter and sort songs"
        aria-expanded={state.filterOpen}
        aria-controls="filter-panel"
        onClick={on(actions.toggleFilters)}
      >
        <span>
          <Icon name="settings" />
        </span>
        <span>Filter & sort</span>
      </button>
      <div className={cx("filter-group", !state.filterOpen)} id="filter-panel">
        <select
          id="format-filter"
          aria-label="Filter by file format"
          value={state.filters.format}
          onChange={(e) => actions.setFilters({ format: e.target.value })}
        >
          {[
            ["", "All formats"],
            ...["FLAC", "WAV", "MP4", "M4A", "MP3"].map((x) => [x, x]),
            ["LIST", "Lists only"],
          ].map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
        <select
          id="genre-filter"
          aria-label="Filter by genre"
          value={state.filters.genre}
          onChange={(e) => actions.setFilters({ genre: e.target.value })}
        >
          <option value="">All genres</option>
          {state.genres.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <select
          id="sort-order"
          aria-label="Sort tracks"
          value={state.filters.sort}
          onChange={(e) =>
            actions.setFilters({ sort: e.target.value as SortOrder })
          }
        >
          {[
            ["added", "Recently added"],
            ["title", "Title A–Z"],
            ["artist", "Artist A–Z"],
            ["bpm", "BPM: low to high"],
          ].map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
        <button
          className="icon-button"
          id="import-list"
          title="Import a shared track list"
          aria-label="Import a shared track list"
          onClick={() => pickFile("list-input")}
        >
          <Icon name="import" />
        </button>
      </div>
    </div>
  );
}
function SelectionBar({ state }: { state: AppSnapshot }) {
  return (
    <div
      className={cx(
        "selection-bar",
        !state.selectionMode && !state.selected.size,
      )}
      id="selection-bar"
    >
      <span id="selection-count">
        {state.selected.size
          ? `${state.selected.size} selected`
          : "Choose a few songs"}
      </span>
      <button
        className={cx(
          "text-button",
          !state.selectionMode || state.filtered.length <= state.pageSize,
        )}
        id="select-matching"
        onClick={on(actions.selectMatching)}
      >
        Select all matches
      </button>
      <div>
        <button
          className="text-button"
          id="edit-selected"
          onClick={on(actions.editSelected)}
        >
          Edit tags
        </button>
        <button
          className="text-button"
          id="add-to-set"
          onClick={on(actions.pickSet)}
        >
          Add to set
        </button>
        <button
          className="text-button"
          id="export-selected"
          onClick={on(actions.exportSelected)}
        >
          Save
        </button>
        <button
          className="text-button"
          id="remove-selected"
          onClick={on(actions.removeSelected)}
        >
          {state.set ? "Remove from set" : "Remove"}
        </button>
        <button
          className="icon-button"
          id="clear-selection"
          aria-label="Clear selection"
          onClick={on(actions.clearSelection)}
        >
          ×
        </button>
      </div>
    </div>
  );
}
export function TrackRow({
  track: t,
  index,
  state,
}: {
  track: Track;
  index: number;
  state: AppSnapshot;
}) {
  const selected = state.selected.has(t.id),
    playing = state.playingId === t.id,
    artwork = state.artworkURLs.get(t.id);
  return (
    <tr
      className={`${selected ? "selected " : ""}${playing ? "playing" : ""}`}
      data-track={t.id}
    >
      <td className="check-cell">
        <input
          type="checkbox"
          className="track-check"
          aria-label={`Select ${t.title}`}
          checked={selected}
          onChange={on(actions.selectTrack, t.id)}
        />
      </td>
      <td className="number-cell">
        {String(state.page * state.pageSize + index + 1).padStart(2, "0")}
      </td>
      <td>
        <div className="track-main">
          <button
            className="track-play"
            aria-label={`Play ${t.title}`}
            onClick={on(actions.playTrack, t)}
          >
            <span className={`cover cover-${t.color % 6}`}>
              {artwork && <img src={artwork} alt="" loading="lazy" />}
              <span className="cover-control">
                <Icon name={playing && !state.paused ? "pause" : "play"} />
              </span>
            </span>
          </button>
          <div className="track-text" onClick={on(actions.playTrack, t)}>
            <strong>{t.title}</strong>
            <small>
              {t.artist || "Unknown artist"}
              {t.demo ? " · Demo track" : ""}
              <span className="format-badge">{formatLabel(t)}</span>
            </small>
          </div>
        </div>
      </td>
      <td className="album-col">{t.album || "—"}</td>
      <td className="bpm-col">{t.bpm || "—"}</td>
      <td className="key-col">
        <span
          className={`key-badge ${index % 3 === 1 ? "blue" : index % 3 === 2 ? "orange" : ""}`}
        >
          {t.key || "—"}
        </span>
      </td>
      <td className="genre-col">{t.genre || "—"}</td>
      <td className="time-col">{duration(t.duration)}</td>
      <td className="end-col">
        <div className="row-actions">
          <button
            className={`icon-button heart-button ${t.favorite ? "liked" : ""}`}
            aria-label={`${t.favorite ? "Unfavorite" : "Favorite"} ${t.title}`}
            aria-pressed={!!t.favorite}
            onClick={on(actions.toggleFavorite, t)}
          >
            <Icon name="heart" />
          </button>
          <button
            className="icon-button more-button"
            aria-label={`Track options for ${t.title}`}
            onClick={on(actions.trackOptions, t)}
          >
            ···
          </button>
        </div>
      </td>
    </tr>
  );
}
function TrackTable({ state }: { state: AppSnapshot }) {
  const all =
      state.visible.length > 0 &&
      state.visible.every((t) => state.selected.has(t.id)),
    some = state.visible.some((t) => state.selected.has(t.id)),
    checkbox = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (checkbox.current) checkbox.current.indeterminate = some && !all;
  }, [some, all]);
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th className="check-cell">
              <input
                ref={checkbox}
                id="select-all"
                type="checkbox"
                aria-label="Select all displayed tracks"
                checked={all}
                onChange={(e) => actions.selectPage(e.target.checked)}
              />
            </th>
            <th className="number-cell">#</th>
            <th>TRACK / ARTIST</th>
            <th className="album-col">ALBUM</th>
            <th className="bpm-col">BPM</th>
            <th className="key-col">KEY</th>
            <th className="genre-col">GENRE</th>
            <th className="time-col">
              <Icon name="clock" />
              <span className="sr-only">Duration</span>
            </th>
            <th className="end-col" />
          </tr>
        </thead>
        <tbody id="track-body">
          {state.visible.map((track, index) => (
            <TrackRow
              key={track.id}
              track={track}
              index={index}
              state={state}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
function Library({ state }: { state: AppSnapshot }) {
  const hide = ["home", "sets", "how-it-works"].includes(state.view),
    hasFilter = !!(
      state.filters.search ||
      state.filters.genre ||
      state.filters.format
    ),
    total = state.filtered.reduce((s, t) => s + (Number(t.duration) || 0), 0);
  return (
    <>
      <div
        className={cx("view-tabs", hide || !!state.set)}
        id="library-views"
        aria-label="Library views"
      >
        {(
          [
            ["all", "All music"],
            ["favorites", "Favorites"],
            ["recent", "Recently added"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            data-view={id}
            className={`view-tab ${state.view === id ? "active" : ""}`}
            onClick={on(actions.navigate, id)}
          >
            {label}
          </button>
        ))}
      </div>
      <Sets state={state} />
      <section
        className={cx("library-section", hide)}
        aria-busy={state.loading}
      >
        <div className="section-heading library-heading">
          <h2 id="table-title">
            {state.set
              ? "Songs"
              : state.view === "favorites"
                ? "Saved songs"
                : "Songs"}{" "}
            <span id="track-count">{state.filtered.length}</span>
          </h2>
          <div className="table-actions">
            <button
              className="button secondary small-button"
              id="select-mode"
              aria-pressed={state.selectionMode}
              onClick={on(actions.toggleSelection)}
            >
              {state.selectionMode ? "Done" : "Select"}
            </button>
            <button
              className={cx("button secondary small-button", !state.set)}
              id="rename-set"
              onClick={on(actions.editSet)}
            >
              <Icon name="edit" /> Set details
            </button>
            <button
              className="button secondary small-button"
              id="share-button"
              onClick={on(actions.shareList)}
            >
              <Icon name="share" /> Share list
            </button>
            <button
              className="button secondary small-button"
              id="export-button"
              onClick={on(actions.exportModal)}
            >
              <Icon name="download" /> Save <span className="chevron">⌄</span>
            </button>
          </div>
        </div>
        <div className={cx("studio-entry", !!state.set)}>
          <button
            className="button secondary"
            id="tag-studio"
            onClick={on(actions.tagStudio)}
          >
            <Icon name="edit" /> Edit song details
          </button>
        </div>
        <Filters state={state} />
        <SelectionBar state={state} />
        <TrackTable state={state} />
        <div
          className={cx("empty-state", !!state.visible.length)}
          id="empty-state"
        >
          <Icon name="music" />
          <h3>{hasFilter ? "No songs found" : "No songs yet"}</h3>
          <p id="empty-message">
            {hasFilter
              ? "Try another search, genre, or file format."
              : state.set
                ? "Tap Add songs to choose from your library or import new music."
                : "Add music or import a shared list to get started."}
          </p>
          <button
            className={cx("button primary", hasFilter)}
            id="empty-add"
            onClick={on(actions.addSongs)}
          >
            Add music
          </button>
        </div>
        <div
          className={cx("pagination", state.filtered.length <= state.pageSize)}
        >
          <button
            className="button secondary small-button"
            id="previous-page"
            disabled={!state.page}
            onClick={on(actions.previousPage)}
          >
            ← Previous
          </button>
          <span id="page-label">
            Page {state.page + 1} of{" "}
            {Math.max(1, Math.ceil(state.filtered.length / state.pageSize))}
          </span>
          <button
            className="button secondary small-button"
            id="next-page"
            disabled={
              (state.page + 1) * state.pageSize >= state.filtered.length
            }
            onClick={on(actions.nextPage)}
          >
            Next →
          </button>
        </div>
        <div className="table-footer">
          <span id="footer-count">
            {state.filtered.length}{" "}
            {state.filtered.length === 1 ? "song" : "songs"}
            {total
              ? ` · ${total >= 3600 ? `${Math.floor(total / 3600)} hr ` : ""}${Math.round((total % 3600) / 60)} min`
              : ""}
          </span>
          <span
            className={cx("", !state.visible.some((t) => t.demo))}
            id="demo-note"
          >
            <i /> Demo track
          </span>
        </div>
      </section>
      <button
        className={cx("backup-card", hide || !!state.set)}
        id="backup-card"
        onClick={on(actions.backupDialog)}
      >
        <span className="backup-card-icon">
          <Icon name="drive" />
        </span>
        <span>
          <strong>Back up library</strong>
        </span>
        <span>
          <Icon name="download" />
        </span>
      </button>
    </>
  );
}
function Player() {
  const state = useSyncExternalStore(
      playbackStore.subscribe,
      playbackStore.getSnapshot,
    ),
    t = state?.track,
    position = state?.duration
      ? Math.min(1000, (state.elapsed / state.duration) * 1000)
      : 0;
  useLayoutEffect(() => {
    document.body.classList.toggle("has-player", !!t);
  }, [!!t]);
  if (!state) return null;
  return (
    <footer
      className={cx("player", !t)}
      id="mini-player"
      style={
        {
          "--player-artwork": state.artwork
            ? `url(${JSON.stringify(state.artwork)})`
            : "none",
        } as React.CSSProperties
      }
    >
      <div className="now-playing">
        <button
          type="button"
          className={`cover player-cover${t ? ` cover-${t.color % 6}` : ""}`}
          id="player-art"
          aria-label="Open full-screen player"
          aria-haspopup="dialog"
          onClick={on(actions.openListening)}
        >
          {state.artwork ? (
            <img src={state.artwork} alt="" />
          ) : (
            <Icon name="music" />
          )}
        </button>
        <button
          className="now-playing-info"
          id="open-listening"
          aria-label="Open full-screen player"
          onClick={on(actions.openListening)}
        >
          <strong id="player-title">
            {t?.title || "Find your next groove"}
          </strong>
          <span id="player-artist">
            {t
              ? t.artist || "Unknown artist"
              : "Add a track to start listening"}
          </span>
        </button>
        <button
          className={cx("icon-button", !t)}
          id="player-favorite"
          aria-label="Favorite current track"
          aria-pressed={!!t?.favorite}
          style={{ color: t?.favorite ? "#ff0155" : "#999999" }}
          onClick={on(actions.favoritePlaying)}
        >
          <Icon name="heart" />
        </button>
      </div>
      <div className="player-center">
        <div className="playback-buttons">
          <button
            className="icon-button"
            id="previous-track"
            aria-label="Previous track"
            onClick={on(actions.previousTrack)}
          >
            <Icon name="previous" />
          </button>
          <button
            className="play-button"
            id="play-pause"
            aria-label={state.paused ? "Play" : "Pause"}
            aria-keyshortcuts="Space"
            title="Play / pause (Space)"
            onClick={on(actions.togglePlayback)}
          >
            <Icon name={state.paused ? "play" : "pause"} />
          </button>
          <button
            className="icon-button"
            id="next-track"
            aria-label="Next track"
            onClick={on(actions.nextTrack)}
          >
            <Icon name="next" />
          </button>
        </div>
        <div className="progress-row">
          <span id="elapsed">
            {state.elapsed > 0 ? duration(state.elapsed) : "0:00"}
          </span>
          <input
            id="progress"
            type="range"
            min="0"
            max="1000"
            value={position}
            disabled={!state.duration}
            aria-label="Playback position"
            aria-valuetext={`${state.elapsed > 0 ? duration(state.elapsed) : "0:00"} of ${state.duration ? duration(state.duration) : "0:00"}`}
            style={{ "--played": `${position / 10}%` } as React.CSSProperties}
            onChange={(e) => actions.seek(e.target.value)}
          />
          <span id="player-duration">
            {state.duration ? duration(state.duration) : "0:00"}
          </span>
        </div>
      </div>
      <div className="player-right">
        <span>
          <Icon name="volume" />
        </span>
        <input
          id="volume"
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={state.volume}
          aria-label="Volume"
          onChange={(e) => actions.setVolume(e.target.value)}
        />
      </div>
    </footer>
  );
}
function FileInputs() {
  const upload =
    (action: (files: File[]) => void | Promise<void>) =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.currentTarget,
        files = Array.from(input.files || []);
      try {
        if (files.length) await action(files);
      } catch (error) {
        actions.reportError(error);
      } finally {
        input.value = "";
      }
    };
  return (
    <>
      <input
        id="audio-input"
        type="file"
        accept={AUDIO_ACCEPT}
        multiple
        hidden
        onChange={upload(actions.importAudio)}
      />
      <input
        id="folder-input"
        type="file"
        accept={AUDIO_ACCEPT}
        webkitdirectory=""
        multiple
        hidden
        onChange={upload(actions.importAudio)}
      />
      <input
        id="list-input"
        type="file"
        accept=".json,application/json"
        hidden
        onChange={upload(async (files) => {
          const file = files[0];
          if (file) await actions.importList(file);
        })}
      />
      <input
        id="metadata-input"
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={upload(async (files) => {
          const file = files[0];
          if (file) await actions.importMetadata(file);
        })}
      />
      <input
        id="backup-input"
        type="file"
        accept=".zip,application/zip"
        hidden
        onChange={upload(async (files) => {
          const file = files[0];
          if (file) await actions.restoreBackup(file);
        })}
      />
    </>
  );
}
function WorkflowDialog() {
  const dialog = useSyncExternalStore(
    dialogStore.subscribe,
    dialogStore.getSnapshot,
  );
  // Existing file-editing workflows bind only inside this isolated React-owned body.
  // Key each workflow so replaced forms cannot retain a previous workflow's controls.
  return (
    <dialog
      id="modal"
      onCancel={(e) => {
        if (!canCloseDialog()) e.preventDefault();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          const r = e.currentTarget.getBoundingClientRect();
          if (
            e.clientX < r.left ||
            e.clientX > r.right ||
            e.clientY < r.top ||
            e.clientY > r.bottom
          )
            actions.closeModal();
        }
      }}
    >
      <div id="modal-content">
        {dialog && (
          <React.Fragment key={dialog.revision}>
            <div className="modal-heading">
              <h2>{dialog.title}</h2>
              <button
                className="icon-button modal-close"
                aria-label="Close dialog"
                onClick={on(actions.closeModal)}
              >
                ×
              </button>
            </div>
            <div
              className="workflow-body"
              dangerouslySetInnerHTML={{ __html: dialog.body }}
            />
          </React.Fragment>
        )}
      </div>
    </dialog>
  );
}
function Toast() {
  const message = useSyncExternalStore(
    toastStore.subscribe,
    toastStore.getSnapshot,
  );
  return (
    <div
      id="toast"
      className={message ? "visible" : ""}
      role="status"
      aria-live="polite"
    >
      {message}
    </div>
  );
}
export function App() {
  const state = useSyncExternalStore(appStore.subscribe, appStore.getSnapshot);
  useLayoutEffect(() => {
    if (!state) return;
    document.body.dataset.page = state.set ? "set" : state.view;
    document.body.classList.toggle("selection-mode", state.selectionMode);
    document.body.classList.toggle("menu-open", state.sidebarOpen);
  }, [state?.view, !!state?.set, state?.selectionMode, state?.sidebarOpen]);
  if (!state) return null;
  const tab =
    state.set || state.view === "sets"
      ? "sets"
      : state.view === "home"
        ? "home"
        : state.view === "how-it-works"
          ? "more"
          : "all";
  return (
    <>
      <Sidebar state={state} />
      <div className="app-shell">
        <Header state={state} />
        <main>
          <PageHeading state={state} />
          <Guide hidden={state.view !== "how-it-works"} actions={actions} />
          <Home state={state} />
          <SetStage state={state} />
          <Library state={state} />
        </main>
      </div>
      <Player />
      <FileInputs />
      <nav className="mobile-tabs" aria-label="Main navigation">
        {(
          [
            ["home", "spark", "Home"],
            ["all", "grid", "Library"],
            ["sets", "list", "Sets"],
            ["more", "help", "More"],
          ] as const
        ).map(([id, icon, label]) => (
          <button
            key={id}
            className={`mobile-tab ${tab === id ? "active" : ""}`}
            id={`mobile-${id === "all" ? "library" : id}`}
            data-tab={id}
            onClick={() => {
              if (id === "more") run(actions.mobileMore);
              else run(actions.navigate, id);
            }}
          >
            <span>
              <Icon name={icon} />
            </span>
            {label}
          </button>
        ))}
      </nav>
      <WorkflowDialog />
      <Toast />
      <div id="drop-overlay" className="hidden">
        <div>
          <span>
            <Icon name="upload" />
          </span>
          <h2>Drop your next discovery.</h2>
          <p>Add audio files to your crate</p>
        </div>
      </div>
    </>
  );
}
